/* ==========================================================================
   dtc-coverflow.js, "The Newest In" ticker physics

   Progressive enhancement. The section ships as a real horizontal
   scroll-snap row holding every new arrival; this file upgrades it into a
   seamless, gently drifting river only when JS runs and the visitor has not
   asked for reduced motion.

   PHYSICS
     drift     24 px/s to the left, a slow luxurious pan, not a marquee
     drag      the track follows the pointer 1:1; drift is suspended
     release   the fling's velocity is carried and decayed by
               vel = target + (vel - target) * 0.94^(dt/16.67)
               so momentum bleeds off and settles back INTO the drift,
               never into a snap, there are no indexes to snap to
     hover     on pointer-fine devices the drift target becomes 0, so the
               river glides to a stop under the cursor and glides back up
               when the cursor leaves
     focus     the same stop, for everyone: while anything inside the
               viewport holds focus the drift target is 0 (WCAG 2.2.2), so
               a keyboard or screen-reader visitor can work along the cards
               instead of chasing them off the left edge, and touch, which
               has no hover, gets a real pause by tapping into the row
     nudge     the arrows (and ←/→) add one card-step of travel, consumed
               with an ease so they slide rather than jump
     wheel     horizontal trackpad deltas scrub the position directly;
               the listener is passive and ignores vertical, so page
               scrolling is never blocked

   THE SEAM
     JS clones the whole card list (as many extra copies as it takes for the
     repeat to be wider than the viewport) and wraps the position modulo the
     width of ONE copy, measured as the distance between the first original
     card and the first clone, so the inter-card gap is included. The pixels
     under the viewport are therefore identical before and after the wrap:
     there is nothing to see, in either direction, however hard it is flung.

   The rAF is cancelled outright, not merely short-circuited, whenever the
   ticker is off screen, the tab is hidden, or it has settled to a stop.

   No libraries. Every rAF, listener and observer is released on cleanup so
   the theme editor can mount and unmount this section freely.
   ========================================================================== */
(function () {
  'use strict';

  var DTC = window.DTC;
  if (!DTC || !DTC.register) return;

  var DRIFT = 24;         // px/s, the headline "gentle continuous scroll"
  var FRICTION = 0.94;    // of the velocity error survives each 16.67ms
  var NUDGE_EASE = 0.90;  // of the remaining arrow-nudge survives each frame
  var MAX_FLING = 4000;   // px/s ceiling so a violent swipe stays readable
  var SLOP = 8;           // px of travel before a press counts as a drag
  /* Fraction of the stage the edge-fade mask takes to transparent. MUST stay
     in step with the mask stops in dtc-coverflow.css, a focused card parked
     inside the fade would render its focus ring at partial alpha, which is
     JS holding content dim. */
  var FADE = 0.05;
  var FOCUS_PAD = 12;     // px of daylight between the fade and a focused card

  DTC.register('coverflow', {
    init: function (root) {
      var stage = root.querySelector('[data-cf-stage]');
      var track = stage && stage.querySelector('[data-cf-track]');
      if (!stage || !track) return;

      var originals = Array.prototype.slice.call(track.querySelectorAll('[data-cf-card]'));
      if (originals.length < 2) return;

      /* Reduced motion → keep the server-rendered scroll-snap row untouched. */
      if (DTC.reducedMotion) return;

      var viewport = root.querySelector('[data-cf-viewport]') || stage;
      var prevBtn = root.querySelector('[data-cf-prev]');
      var nextBtn = root.querySelector('[data-cf-next]');

      var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

      var pos = 0;        // px of travel; grows as the river flows left
      var vel = DRIFT;    // px/s
      var nudge = 0;      // px of arrow travel still owed
      var copyW = 0;      // width of exactly one copy of the card list
      var cardStep = 0;   // one card + one gap
      var clones = [];

      var dragging = false, moved = false, pointerId = null, captured = false;
      var startX = 0, lastX = 0, lastT = 0;
      var hovering = false, visible = true, focused = false;
      var raf = 0, lastFrame = 0, resizeTimer = 0, lastW = 0;

      root.classList.add('is-live');

      /* Base mode may have left the row scrolled, and live mode owns the
         offset, but `overflow: hidden` is still a scroll container and the
         transformed track contributes scrollable overflow, so any UA
         scroll-into-view (focus, find-in-page, a bfcache restore) can shift
         scrollLeft to a value the transform arithmetic knows nothing about.
         Pin it, don't just zero it once. */
      function pinScroll() {
        if (stage.scrollLeft !== 0) stage.scrollLeft = 0;
      }
      pinScroll();

      /* --- geometry ----------------------------------------------------- */

      /* A clone control must never take focus: focus inside an
         aria-hidden="true" subtree makes Chrome strip the aria-hidden, which
         would expose every duplicated shirt to assistive tech. Killing the
         default of mousedown stops the browser focusing the control and
         leaves the click that follows completely untouched, the card under
         the cursor is usually a clone, so it has to stay clickable. */
      function preventFocus(e) { e.preventDefault(); }

      /* Clones are decorative duplicates: the a11y tree and the tab order
         must still see each shirt exactly once. */
      function appendCopy() {
        var set = [];
        for (var i = 0; i < originals.length; i++) {
          var c = originals[i].cloneNode(true);
          c.setAttribute('aria-hidden', 'true');
          c.setAttribute('data-cf-clone', '');
          c.removeAttribute('data-index');

          /* Duplicate ids would poison getElementById and every label/aria
             reference on the page. The card markup ships none today; a
             placeholder SVG or a future edit could, so strip them outright
             rather than trusting the template to stay id-free. */
          if (c.id) c.removeAttribute('id');
          var ided = c.querySelectorAll('[id]');
          for (var k = 0; k < ided.length; k++) ided[k].removeAttribute('id');

          var f = c.querySelectorAll('a[href], button');
          for (var j = 0; j < f.length; j++) {
            f[j].setAttribute('tabindex', '-1');
            f[j].addEventListener('mousedown', preventFocus);
          }
          track.appendChild(c);
          set.push(c);
        }
        return set;
      }

      function dropClones() {
        for (var i = 0; i < clones.length; i++) {
          if (clones[i].parentNode) clones[i].parentNode.removeChild(clones[i]);
        }
        clones = [];
      }

      function measure() {
        dropClones();

        var first = appendCopy();
        clones = first.slice();

        /* Distance between a card and its own clone IS one copy, the gap
           that follows the last card is baked in, so no seam pixel is lost
           or doubled. */
        copyW = first[0].offsetLeft - originals[0].offsetLeft;
        cardStep = originals[1].offsetLeft - originals[0].offsetLeft;
        lastW = stage.clientWidth;

        /* How many EXTRA copies the tail needs.
           k copies span k*copyW MINUS one gap, no gap follows the last card,
           while pos ranges over [0, copyW), so the window's right edge reaches
           copyW + clientWidth. The tail therefore only covers the window when
           need*copyW >= clientWidth + gap. Since copyW is always wider than a
           single gap, ceil(clientWidth / copyW) + 1 clears that bound at every
           viewport and product count, and leaves a whole spare copy in hand:
           an extra copy is cheap, a stripe of bare --paper at the right edge
           once per cycle is not. */
        var need = copyW > 0 ? Math.ceil(stage.clientWidth / copyW) + 1 : 0;
        for (var n = 1; n < need; n++) clones = clones.concat(appendCopy());

        if (copyW > 0) {
          pos = wrap(pos);
          paint();
        }
      }

      function wrap(p) {
        /* Nothing measurable yet (the section, or an ancestor, was
           display:none). Hand the position back untouched instead of pinning
           it to 0, shouldRun() halts the loop until measure() succeeds. */
        if (!(copyW > 0)) return p;
        return ((p % copyW) + copyW) % copyW;
      }

      function paint() {
        track.style.transform = 'translate3d(' + (-pos).toFixed(2) + 'px, 0, 0)';
      }

      /* --- loop --------------------------------------------------------- */

      /* Under a fine pointer, hovering parks the river instead of merely
         freezing it, a hard freeze mid-glide reads as a bug. Focus parks it
         for EVERYONE, which is the only stop a keyboard, screen-reader or
         touch visitor has (WCAG 2.2.2, hover exists on neither of the last
         two). It glides back up the moment focus leaves. */
      function target() {
        return ((hovering && finePointer) || focused) ? 0 : DRIFT;
      }

      function shouldRun() {
        /* Un-measured: the section was hidden when we started, so there is no
           geometry to move. Never burn 60fps repainting translate3d(0,0,0). */
        if (!(copyW > 0)) return false;
        /* A drag paints from onMove, so the loop has nothing to do while a
           finger is down, running it anyway is 60fps of pure battery. */
        if (dragging) return false;
        if (!visible || document.hidden) return false;
        return target() !== 0 || Math.abs(vel) > 0.05 || nudge !== 0;
      }

      function sync() {
        if (shouldRun()) {
          if (!raf) { lastFrame = 0; raf = requestAnimationFrame(tick); }
        } else if (raf) {
          cancelAnimationFrame(raf);
          raf = 0;
        }
      }

      function tick(ts) {
        /* A backgrounded tab can hand us a huge dt; clamp it so the river
           never teleports on the frame after it wakes up. */
        var dt = lastFrame ? Math.min(ts - lastFrame, 50) : 16.67;
        lastFrame = ts;
        var f = dt / 16.6667;

        if (!dragging) {
          var t = target();
          vel = t + (vel - t) * Math.pow(FRICTION, f);
          if (Math.abs(vel - t) < 0.05) vel = t;
          pos += vel * dt / 1000;

          if (nudge) {
            var step = Math.abs(nudge) < 0.5 ? nudge : nudge * (1 - Math.pow(NUDGE_EASE, f));
            pos += step;
            nudge -= step;
            if (Math.abs(nudge) < 0.5) nudge = 0;
          }

          pos = wrap(pos);
          paint();
        }

        if (dragging || !shouldRun()) { raf = 0; return; }
        raf = requestAnimationFrame(tick);
      }

      /* --- drag --------------------------------------------------------- */

      function onDown(e) {
        if (e.button != null && e.button !== 0) return;
        /* A second finger must not restart (or hijack) a drag already in
           flight; the first pointer keeps the river until it lets go. */
        if (dragging) return;
        dragging = true;
        moved = false;
        startX = lastX = e.clientX;
        lastT = e.timeStamp || Date.now();
        vel = 0;
        nudge = 0;
        pointerId = e.pointerId;
        captured = false;
        /* Do NOT take pointer capture here. Capturing on pointerdown retargets
           the follow-up click to the stage, so a plain tap never reaches the
           card's own <a> and the product / choose-options link does nothing.
           We capture in onMove instead, once the travel passes SLOP and the
           gesture is unambiguously a drag, a tap is left completely alone. */
        root.classList.add('is-dragging');
        sync();
      }

      function onMove(e) {
        /* Only the captured pointer moves the river. Without this, a second
           finger tapping elsewhere hands us the INTER-FINGER distance as the
           delta, hundreds of px, which teleports the track and launches vel
           at the fling ceiling. */
        if (!dragging || e.pointerId !== pointerId) return;
        var x = e.clientX;
        var t = e.timeStamp || Date.now();
        var dx = x - lastX;

        if (Math.abs(x - startX) > SLOP) {
          moved = true;
          /* Now it's a drag: grab the pointer so the river keeps tracking even
             when the finger leaves the stage. Deferred to here so taps never
             capture (see onDown). */
          if (!captured) {
            captured = true;
            try { stage.setPointerCapture(pointerId); } catch (_) {}
          }
        }

        pos = wrap(pos - dx);   /* 1:1 with the finger */
        paint();

        var ms = t - lastT;
        if (ms > 0) {
          /* Smoothed so one jittery sample cannot launch the river. */
          vel = vel * 0.7 + (-dx / ms * 1000) * 0.3;
        }
        lastX = x;
        lastT = t;
      }

      function endDrag(fling) {
        if (!dragging) return;
        dragging = false;
        root.classList.remove('is-dragging');
        if (pointerId != null) {
          if (captured) { try { stage.releasePointerCapture(pointerId); } catch (_) {} }
          pointerId = null;
          captured = false;
        }
        if (!fling) {
          vel = target();
          /* A cancelled gesture, the page scroller took the touch, or the
             capture was lost, never produces a click, so the suppression
             flag must die with it. Left standing it would swallow whatever
             click arrived next, and the next click to reach the stage with no
             pointerdown before it is a KEYBOARD one. */
          moved = false;
        } else {
          vel = Math.max(-MAX_FLING, Math.min(MAX_FLING, vel));
        }
        lastFrame = 0;
        sync();
      }

      /* A stray pointerup from a second finger must not end finger one's
         drag. When no drag is running the id is moot, endDrag() no-ops. */
      function onUp(e) { if (dragging && e.pointerId !== pointerId) return; endDrag(true); }
      function onCancel(e) { if (dragging && e.pointerId !== pointerId) return; endDrag(false); }

      /* A drag must never fire the click that a tap would. Capture phase so
         it beats both the card link and the delegated quick-add in dtc-ui.js.
         A real tap never trips SLOP, so taps are untouched. */
      function onClickCapture(e) {
        /* A synthetic activation. Enter on a card link, Space on the
           quick-add, an assistive-tech click, carries no click count and no
           originating pointer; a real tap or mouse click carries at least one
           of the two. None of the synthetic ones can follow a drag, and
           swallowing one costs the visitor the entire interaction
           (stopPropagation also kills the delegated quick-add in dtc-ui.js),
           so they are never suppressed. */
        if (!e.detail && (!('pointerType' in e) || !e.pointerType)) return;
        if (!moved) return;
        moved = false;
        e.preventDefault();
        e.stopPropagation();
      }

      /* --- pointers, wheel, keys, arrows -------------------------------- */

      function onEnter() { hovering = true; lastFrame = 0; sync(); }
      function onLeave() { hovering = false; lastFrame = 0; sync(); }

      /* Horizontal trackpad wheel scrubs the river. Passive, and vertical
         deltas are ignored outright, so page scroll is never blocked. */
      function onWheel(e) {
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
        pos = wrap(pos + e.deltaX);
        paint();
        lastFrame = 0;
        sync();
      }

      function nudgeBy(px) {
        /* A zero step (nothing measured yet) would only wake the loop to do
           nothing. Ask for a measurement instead. */
        if (!px) { if (!(copyW > 0)) { measure(); sync(); } return; }
        nudge += px;
        lastFrame = 0;
        sync();
      }

      function onKey(e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); nudgeBy(-cardStep); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); nudgeBy(cardStep); }
      }

      /* Keyboard focus can land on a card that has drifted out of sight,
         only reposition when it actually is out of sight, so tabbing along a
         visible run of cards does not yank the river. */
      function onFocusIn(e) {
        var t = e.target;

        /* Focus must never rest inside an aria-hidden="true" clone: Chrome
           answers that by stripping the aria-hidden, exposing every duplicate
           to assistive tech. preventFocus() in appendCopy() is the brace;
           this is the belt (a UA that focuses on mouseup, say). */
        if (t.closest && t.closest('[data-cf-clone]')) {
          if (t.blur) t.blur();
          return;
        }

        /* KEYBOARD focus only. Chrome and Firefox focus an <a href> on
           mousedown, so a plain grab would otherwise (a) jolt the track the
           instant the pointer went down, if the grabbed card sat in a fade
           band, and (b) latch `focused` on that link forever, the river
           would never restart after a mouse drag, reading as "it broke when
           I touched it". :focus-visible is exactly the "did this come from
           the keyboard" signal; mice do not match it, keyboards and AT do.
           Fine pointers already have the hover park, and a finger already
           parks the river by holding it. */
        var kb = true;
        try { kb = !t.matches || t.matches(':focus-visible'); } catch (_) { kb = true; }
        if (!kb) return;

        /* Hold the river still for as long as focus lives in here. Without
           it, focusing a card parks it and the drift immediately carries it
           away, a 300px card clears the left edge in ~13s, and focusin
           never fires again to bring it back. */
        focused = true;
        lastFrame = 0;

        var card = t.closest && t.closest('[data-cf-card]');
        if (card && copyW > 0) {
          /* The mask takes the outer FADE of the stage to transparent, so
             "visible" stops short of the stage edges: a card parked at 24px
             would render its left edge and its focus ring at partial alpha,
             i.e. JS holding content dim. Test against the fades and park
             clear of them. */
          var fade = stage.clientWidth * FADE;
          var left = card.offsetLeft - originals[0].offsetLeft;
          var rel = wrap(left - pos);
          if (rel < fade || rel + card.offsetWidth > stage.clientWidth - fade) {
            pos = wrap(left - fade - FOCUS_PAD);
            paint();
          }
        }
        sync();
      }

      /* Focus moving from one card to the next keeps the hold; only a real
         exit, including focus leaving the document, releases it. */
      function onFocusOut(e) {
        if (e.relatedTarget && viewport.contains(e.relatedTarget)) return;
        focused = false;
        lastFrame = 0;
        sync();
      }

      function onResize() {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(function () { measure(); sync(); }, 150);
      }

      function onVisibility() { lastFrame = 0; sync(); }

      /* --- wiring ------------------------------------------------------- */

      var offs = [];
      offs.push(DTC.on(stage, 'pointerdown', onDown));
      offs.push(DTC.on(stage, 'pointermove', onMove));
      offs.push(DTC.on(stage, 'pointerup', onUp));
      offs.push(DTC.on(stage, 'pointercancel', onCancel));
      offs.push(DTC.on(stage, 'lostpointercapture', onCancel));
      offs.push(DTC.on(stage, 'click', onClickCapture, true));
      offs.push(DTC.on(stage, 'keydown', onKey));
      offs.push(DTC.on(stage, 'wheel', onWheel, { passive: true }));
      offs.push(DTC.on(stage, 'scroll', pinScroll, { passive: true }));
      offs.push(DTC.on(stage, 'dragstart', function (e) { e.preventDefault(); }));
      /* Focus is watched on the VIEWPORT, not the stage, so the two arrows
         count as "inside" too: tabbing (or tapping) onto an arrow holds the
         river still while it is being used. */
      offs.push(DTC.on(viewport, 'focusin', onFocusIn));
      offs.push(DTC.on(viewport, 'focusout', onFocusOut));
      offs.push(DTC.on(viewport, 'pointerenter', onEnter));
      offs.push(DTC.on(viewport, 'pointerleave', onLeave));
      offs.push(DTC.on(window, 'resize', onResize, { passive: true }));
      offs.push(DTC.on(document, 'visibilitychange', onVisibility));

      if (prevBtn) offs.push(DTC.on(prevBtn, 'click', function () { nudgeBy(-cardStep); }));
      if (nextBtn) offs.push(DTC.on(nextBtn, 'click', function () { nudgeBy(cardStep); }));

      /* Pause entirely while the ticker is off screen. */
      var io = null;
      if ('IntersectionObserver' in window) {
        io = new IntersectionObserver(function (entries) {
          visible = entries[entries.length - 1].isIntersecting;
          /* First sight of a section that was hidden at init: every
             offsetLeft was 0 then, so there is still nothing measured. */
          if (visible && !(copyW > 0)) measure();
          lastFrame = 0;
          sync();
        }, { rootMargin: '120px 0px' });
        io.observe(stage);
      }

      /* Neither `resize` nor `load` fires when an element merely becomes
         visible, and the theme editor mounts sections inside display:none
         all the time, where every offsetLeft reads 0. A ResizeObserver on the
         stage is the direct signal that geometry finally exists. */
      var ro = null;
      if ('ResizeObserver' in window) {
        ro = new ResizeObserver(function () {
          if (copyW > 0 && stage.clientWidth === lastW) return;
          onResize();
        });
        ro.observe(stage);
      }

      measure();
      /* Product photos are lazy, remeasure once they have settled, in case
         a late layout shift moved the seam. */
      offs.push(DTC.on(window, 'load', function () { measure(); sync(); }));
      sync();

      /* --- cleanup (theme editor mounts/unmounts constantly) ------------- */
      return function cleanup() {
        if (raf) cancelAnimationFrame(raf);
        raf = 0;
        clearTimeout(resizeTimer);
        /* Unmounted mid-drag: the capture outlives the section unless we hand
           it back, and the pointer would stay bound to a dead element. */
        if (pointerId != null) {
          try { stage.releasePointerCapture(pointerId); } catch (_) {}
          pointerId = null;
        }
        dragging = false;
        if (io) io.disconnect();
        if (ro) ro.disconnect();
        offs.forEach(function (off) { off(); });
        offs.length = 0;
        dropClones();
        root.classList.remove('is-live', 'is-dragging');
        track.style.removeProperty('transform');
      };
    }
  });
}());
